import {
  ElementType,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from "embla-carousel-react";
import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import { classNames } from "@ct-react/core";
import { useMediaQueryDown } from "../../../hooks/media-queries";
import "./scrollable-items.scss";

type HeadProps = { title?: string, subTitle?: string, children?: ReactNode };

type Props = {
  as?: ElementType;
  className?: string;
  children: [ ReactElement<any, typeof ScrollableHeader>, ReactElement<any, typeof ScrollableContent> ] |
    [ ReactElement<any, typeof ScrollableHeader>, ReactElement<any, typeof ScrollableContent>, ReactElement<any, typeof ScrollableFooter> ];
}

export const ScrollableHeader = (_: HeadProps) => null;
export const ScrollableContent = ({ children }: PropsWithChildren) => children;
export const ScrollableFooter = (_: PropsWithChildren) => null;

const ScrollableList = ({ as: Tag = "div", className, children }: Props) => {

  const [ emblaRef, emblaApi ] = useEmblaCarousel({
    align: "start",
    skipSnaps: true,
    breakpoints: {
      "(min-width: 768px)": { slidesToScroll: 2 }
    }
  });

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPreviousable(emblaApi.canScrollPrev());
    setNextable(emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
  }, [ emblaApi, onSelect ]);

  const [ previousable, setPreviousable ] = useState<boolean>(false);
  const [ nextable, setNextable ] = useState<boolean>(false);

  const scroll = useCallback((direction: -1 | 1) => {
    !!emblaApi && (direction < 0 ? emblaApi.scrollPrev() : emblaApi.scrollNext());
  }, [ emblaApi ]);

  const isMobileOnly = useMediaQueryDown("mobile");
  const wrapperClasses = classNames("wrap", "touch-scroll", className);

  const [ { props: { title, subTitle, children: complement } }, content, footer ] = children;

  useEffect(() => {
    (!!complement && !!emblaApi) && emblaApi.scrollTo(0);
  }, [ complement ]);

  const navRendering = useMemo(() => (
    <div className="scroll-nav">
      <button type="button"
              onClick={() => scroll(-1)}
              disabled={!previousable}>
        <IconArrowLeft className="a-icon" />
      </button>
      <button type="button"
              onClick={() => scroll(1)}
              disabled={!nextable}>
        <IconArrowRight className="a-icon" />
      </button>
    </div>
  ), [ previousable, nextable ]);

  return (
    <Tag className={wrapperClasses}>
      <div className="scroll-header">
        <div className="labels">
          {!!subTitle && <span className="sub">{subTitle}</span>}
          {!!title && <h2>{title}</h2>}
          {complement}
        </div>
        {!isMobileOnly && navRendering}
      </div>
      <div ref={emblaRef} className="scroll-content">
        <div className="scroll-track">{content}</div>
      </div>
      {isMobileOnly && navRendering}
      {!!footer &&
        <div className="scroll-footer">{footer.props.children}</div>
      }
    </Tag>);

}

export default ScrollableList;
